// src/pages/CreateHunt.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { CameraCapture } from '../components/CameraCapture'
import { BunnyCharacter } from '../components/BunnyCharacter'
import { api } from '../api/client'
import { compressImage } from '../lib/compressImage'

interface EggDraft {
  description: string
  difficulty: number
  photo_base64: string
  photo_key: string
}

export function CreateHunt() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'name' | 'eggs' | 'generating' | 'preview'>('name')
  const [huntName, setHuntName] = useState('')
  const [eggs, setEggs] = useState<EggDraft[]>([])
  const [currentEgg, setCurrentEgg] = useState<Partial<EggDraft>>({})
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAddEgg = () => {
    if (!currentEgg.photo_base64 || !currentEgg.description?.trim()) return

    setEggs((prev) => [
      ...prev,
      {
        description: currentEgg.description!.trim(),
        difficulty: currentEgg.difficulty || 1,
        photo_base64: currentEgg.photo_base64!,
        photo_key: '',
      },
    ])
    setCurrentEgg({})
  }

  const handleFinish = async () => {
    setStep('generating')
    setGenerating(true)
    setError(null)

    try {
      // Compress + upload all reference photos
      const uploadedEggs = await Promise.all(
        eggs.map(async (egg) => {
          const compressed = await compressImage(egg.photo_base64)
          const { key } = await api.photos.upload(compressed)
          return { ...egg, photo_key: key }
        })
      )

      // Create the hunt
      const { hunt_id } = await api.hunts.create({
        name: huntName,
        eggs: uploadedEggs.map((e) => ({
          description: e.description,
          difficulty: e.difficulty,
          photo_key: e.photo_key,
        })),
        config: { children_ages: [7, 9] },
      })

      // Generate AI content
      await api.generate(
        hunt_id,
        uploadedEggs.map((e) => ({ description: e.description, difficulty: e.difficulty })),
        { children_ages: [7, 9] }
      )

      // Activate the hunt
      await api.hunts.update(hunt_id, { status: 'active' })

      setStep('preview')
    } catch (e) {
      setError('AI-genereringen misslyckades. Kontrollera att API-nyckeln är korrekt konfigurerad.')
      // Stay on generating step to show error with retry option
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="min-h-dvh p-6 bg-cream">
      <div className="max-w-lg mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/parent')} className="mb-4">
          ← Tillbaka
        </Button>

        <AnimatePresence mode="wait">
          {step === 'name' && (
            <motion.div
              key="name"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex flex-col gap-6"
            >
              <h1 className="font-heading text-2xl text-ink">Namnge din jakt</h1>
              <input
                type="text"
                value={huntName}
                onChange={(e) => setHuntName(e.target.value)}
                placeholder="t.ex. Påsk hemma 2026"
                className="w-full px-4 py-3 rounded-2xl border-2 border-turquoise/30 font-body text-lg
                           focus:border-turquoise focus:outline-none bg-white"
              />
              <Button
                size="lg"
                disabled={!huntName.trim()}
                onClick={() => setStep('eggs')}
              >
                Nästa
              </Button>
            </motion.div>
          )}

          {step === 'eggs' && (
            <motion.div
              key="eggs"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex flex-col gap-6"
            >
              <h1 className="font-heading text-2xl text-ink">
                Lägg till ägg ({eggs.length} tillagda)
              </h1>

              {error && (
                <p className="text-coral font-body">{error}</p>
              )}

              {/* Existing eggs */}
              {eggs.map((egg, i) => (
                <Card key={i}>
                  <div className="flex gap-3 items-center">
                    <img
                      src={egg.photo_base64}
                      alt={`Ägg ${i + 1}`}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    <div>
                      <p className="font-heading text-base">Ägg {i + 1}</p>
                      <p className="font-body text-sm text-ink/60">{egg.description}</p>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Add new egg */}
              <Card className="border-2 border-dashed border-turquoise/30">
                <h3 className="font-heading text-lg mb-3">Nytt ägg</h3>

                <CameraCapture
                  label="Fota ägget på sin plats"
                  onCapture={(base64) => setCurrentEgg((prev) => ({ ...prev, photo_base64: base64 }))}
                />

                {currentEgg.photo_base64 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 flex flex-col gap-3"
                  >
                    <input
                      type="text"
                      value={currentEgg.description || ''}
                      onChange={(e) =>
                        setCurrentEgg((prev) => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="Beskriv kort, t.ex. 'Under soffkudden'"
                      className="w-full px-4 py-3 rounded-2xl border-2 border-turquoise/30 font-body text-lg
                                 focus:border-turquoise focus:outline-none bg-white"
                    />

                    <div className="flex gap-2 items-center">
                      <span className="font-body text-base text-ink/60">Svårighet:</span>
                      {[1, 2, 3].map((d) => (
                        <button
                          key={d}
                          onClick={() => setCurrentEgg((prev) => ({ ...prev, difficulty: d }))}
                          className={`w-10 h-10 rounded-full font-heading text-lg
                            ${(currentEgg.difficulty || 1) === d
                              ? 'bg-coral text-white'
                              : 'bg-white text-ink/40'
                            }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>

                    <Button
                      variant="secondary"
                      disabled={!currentEgg.description?.trim()}
                      onClick={handleAddEgg}
                    >
                      + Lägg till ägg
                    </Button>
                  </motion.div>
                )}
              </Card>

              {eggs.length > 0 && (
                <Button size="lg" onClick={handleFinish}>
                  Skapa jakt! ({eggs.length} ägg)
                </Button>
              )}
            </motion.div>
          )}

          {step === 'generating' && (
            <motion.div
              key="generating"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center gap-6 mt-20"
            >
              <BunnyCharacter size="lg" animate={error ? 'idle' : 'think'} />
              {error ? (
                <>
                  <p className="font-body text-base text-coral text-center">{error}</p>
                  <Button size="lg" onClick={handleFinish}>
                    Forsok igen
                  </Button>
                </>
              ) : (
                <>
                  <p className="font-heading text-xl text-ink text-center">
                    Påskharen skriver berättelsen...
                  </p>
                  <p className="font-body text-base text-ink/50 text-center">
                    Skapar ledtrådar, rim och äventyr
                  </p>
                </>
              )}
            </motion.div>
          )}

          {step === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-6 mt-10"
            >
              <BunnyCharacter size="lg" animate="celebrate" />
              <h1 className="font-heading text-2xl text-ink text-center">
                Jakten är redo!
              </h1>
              <p className="font-body text-lg text-ink/60 text-center">
                "{huntName}" med {eggs.length} ägg. Dela PIN-koden med barnen
                så kan de börja spela!
              </p>
              <Button size="lg" onClick={() => navigate('/parent')}>
                Tillbaka till panelen
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
